import type * as React from 'react';

export const SIDEBAR_COOKIE_NAME = 'sidebar_state';
export const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
export const SIDEBAR_WIDTH = '13.75rem';
export const SIDEBAR_WIDTH_MOBILE = '18rem';
export const SIDEBAR_WIDTH_ICON = '3rem';
export const SIDEBAR_KEYBOARD_SHORTCUT = 'b';
// Long enough that a hover peek expands the sidebar before a tooltip could open.
export const SIDEBAR_TOOLTIP_DELAY_MS = 300;
// Grace period so brushing past the edge, or crossing into portalled content,
// does not collapse the sidebar mid-interaction.
export const SIDEBAR_HOVER_COLLAPSE_DELAY_MS = 150;

export enum SidebarState {
	Expanded = 'expanded',
	Collapsed = 'collapsed',
}

export enum SidebarSide {
	Left = 'left',
	Right = 'right',
}

export enum SidebarVariant {
	Default = 'sidebar',
	Floating = 'floating',
	Inset = 'inset',
}

export enum SidebarCollapsible {
	Offcanvas = 'offcanvas',
	Icon = 'icon',
	None = 'none',
}

export enum SidebarMenuSubButtonSize {
	Small = 'sm',
	Medium = 'md',
}

export interface ICssVariableStyle extends React.CSSProperties {
	[cssVariable: `--${string}`]: string | number | undefined;
}

export interface ISidebarContext {
	state: SidebarState;
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	isHoverExpanded: boolean;
	setIsHoverExpanded: (isHoverExpanded: boolean) => void;
	isMobileOpen: boolean;
	setIsMobileOpen: (isMobileOpen: boolean) => void;
	isMobile: boolean;
	toggleSidebar: () => void;
	mobileWidth: string;
}

export interface ISidebarProviderProps extends React.ComponentProps<'div'> {
	defaultOpen?: boolean;
	isOpen?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
	/** Width of the sidebar while expanded. Defaults to `SIDEBAR_WIDTH`. */
	width?: string;
	/**
	 * Width of the collapsed icon rail, used when the sidebar is rendered with
	 * `collapsible={SidebarCollapsible.Icon}`. Defaults to `SIDEBAR_WIDTH_ICON`.
	 */
	collapsedWidth?: string;
	/**
	 * Width of the sheet the sidebar becomes below the `md` breakpoint.
	 * Defaults to `SIDEBAR_WIDTH_MOBILE`.
	 */
	mobileWidth?: string;
}

export interface ISidebarProps extends React.ComponentProps<'div'> {
	side?: SidebarSide;
	variant?: SidebarVariant;
	collapsible?: SidebarCollapsible;
}

export interface ISidebarHeaderProps extends React.ComponentProps<'div'> {
	hasTrigger?: boolean;
}

export interface ISidebarMenuSubButtonProps extends React.ComponentProps<'a'> {
	asChild?: boolean;
	size?: SidebarMenuSubButtonSize;
	isActive?: boolean;
}
